import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import PageLayout from "../../components/common/page-layout";
import {
  useUserSessionsAdminCompanyUsersQuery,
  useUserSessionsAdminRevokeSessionsMutation,
  useUserSessionsAdminSearchCompaniesQuery,
} from "../../generated/graphql";
import useIsInternalAdmin from "../api-keys-admin/hooks/use-is-internal-admin";
import {
  describeRevokeTarget,
  toRevokeTargetUuids,
  toggleSelectAll,
} from "./revoke-target";

const COMPANY_SEARCH_LIMIT = 25;
const SEARCH_DEBOUNCE_MS = 300;

const styles = {
  page: { p: 3 } satisfies SxProps<Theme>,
  centered: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    p: 4,
  } satisfies SxProps<Theme>,
  searchRow: { mb: 2, maxWidth: 480 } satisfies SxProps<Theme>,
  columns: { alignItems: "flex-start" } satisfies SxProps<Theme>,
  companyList: {
    minWidth: 320,
    maxHeight: 520,
    overflow: "auto",
  } satisfies SxProps<Theme>,
  usersPanel: { flex: 1 } satisfies SxProps<Theme>,
  clickableRow: { cursor: "pointer" } satisfies SxProps<Theme>,
  actions: { mt: 2, alignItems: "center" } satisfies SxProps<Theme>,
};

const SUBTITLE =
  "Ends live sessions immediately. Archiving on its own only blocks the next login, so a user who never closes their browser stays signed in until their token expires.";

/**
 * Internal tool for ending a customer's sessions, used when an account is shut
 * off for non-payment.
 *
 * Archiving sets `isActive: false`, which blocks the next login. Revoking
 * invalidates the JWT the user already holds, which archiving alone cannot
 * reach. The page offers both because either on its own leaves a gap.
 */
const UserSessionsAdminPage = () => {
  const { canAccess, loading: permissionsLoading } = useIsInternalAdmin();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState<string | null>(
    null,
  );
  const [selectedUserUuids, setSelectedUserUuids] = useState<string[]>([]);
  const [archiveUsers, setArchiveUsers] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // The company search is an unindexable ILIKE, so it is debounced rather than
  // fired on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // The app-wide defaults are `errorPolicy: "all"` for mutations and "ignore"
  // for watch queries (apollo-client.ts), which resolve on a GraphQL error
  // instead of throwing. On a destructive tool a silent failure is the worst
  // outcome, so every operation here opts back into throwing.
  const {
    data: companiesData,
    loading: companiesLoading,
    error: companiesError,
  } = useUserSessionsAdminSearchCompaniesQuery({
    errorPolicy: "none",
    variables: {
      input: {
        searchTerm:
          debouncedSearchTerm.trim() === "" ? null : debouncedSearchTerm,
        limit: COMPANY_SEARCH_LIMIT,
      },
    },
  });

  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useUserSessionsAdminCompanyUsersQuery({
    errorPolicy: "none",
    variables: { companyUuid: selectedCompanyUuid ?? "" },
    skip: selectedCompanyUuid === null,
  });

  const [
    revokeSessions,
    { data: revokeData, loading: revoking, reset: resetRevokeResult },
  ] = useUserSessionsAdminRevokeSessionsMutation({ errorPolicy: "none" });

  const companies = useMemo(
    () => companiesData?.userSessionsAdminSearchCompanies ?? [],
    [companiesData],
  );
  const users = useMemo(
    () => usersData?.userSessionsAdminCompanyUsers.users ?? [],
    [usersData],
  );
  const totalCount =
    usersData?.userSessionsAdminCompanyUsers.totalCount ?? users.length;
  const hasMore = usersData?.userSessionsAdminCompanyUsers.hasMore ?? false;
  const loadedUuids = useMemo(() => users.map((user) => user.uuid), [users]);
  const selectedCompany = useMemo(
    () => companies.find((company) => company.uuid === selectedCompanyUuid),
    [companies, selectedCompanyUuid],
  );

  const handleSelectCompany = useCallback(
    (companyUuid: string) => {
      setSelectedCompanyUuid(companyUuid);
      setSelectedUserUuids([]);
      setErrorMessage(null);
      // Otherwise the previous company's result banner stays on screen while a
      // different company's users are displayed.
      resetRevokeResult();
    },
    [resetRevokeResult],
  );

  const handleToggleUser = useCallback((userUuid: string) => {
    setSelectedUserUuids((current) =>
      current.includes(userUuid)
        ? current.filter((uuid) => uuid !== userUuid)
        : [...current, userUuid],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedUserUuids((current) =>
      toggleSelectAll({ selected: current, loadedUuids }),
    );
  }, [loadedUuids]);

  const handleConfirmRevoke = useCallback(async () => {
    if (selectedCompanyUuid === null) {
      return;
    }
    setErrorMessage(null);
    try {
      await revokeSessions({
        variables: {
          input: {
            companyUuid: selectedCompanyUuid,
            userUuids: toRevokeTargetUuids(selectedUserUuids),
            archiveUsers,
          },
        },
      });
      await refetchUsers();
      setSelectedUserUuids([]);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Could not revoke sessions. Try again.",
      );
    } finally {
      setConfirmOpen(false);
    }
  }, [
    archiveUsers,
    refetchUsers,
    revokeSessions,
    selectedCompanyUuid,
    selectedUserUuids,
  ]);

  if (permissionsLoading) {
    return (
      <PageLayout title="User sessions">
        <Box sx={styles.centered}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!canAccess) {
    return (
      <PageLayout title="User sessions">
        <Box sx={styles.page}>
          <Alert severity="error">
            You need the INTERNAL_TOOLS write permission to use this tool.
          </Alert>
        </Box>
      </PageLayout>
    );
  }

  const result = revokeData?.userSessionsAdminRevokeSessions;
  const targetDescription = describeRevokeTarget({
    selectedCount: selectedUserUuids.length,
    totalCount,
    companyName: selectedCompany?.name ?? null,
  });

  return (
    <PageLayout subtitle={SUBTITLE} title="User sessions">
      <Box sx={styles.page}>
        {errorMessage !== null && (
          <Alert
            severity="error"
            onClose={() => {
              setErrorMessage(null);
            }}
          >
            {errorMessage}
          </Alert>
        )}

        {companiesError !== undefined && (
          <Alert severity="error">
            Could not load companies. {companiesError.message}
          </Alert>
        )}

        {usersError !== undefined && (
          <Alert severity="error">
            Could not load users. {usersError.message}
          </Alert>
        )}

        {result !== undefined && result !== null && (
          <Alert
            severity={
              result.skippedNoEmailCount > 0 || result.failedCount > 0
                ? "warning"
                : "success"
            }
          >
            Revoked {result.revokedCount}, archived {result.archivedCount}.
            {result.skippedNoEmailCount > 0 &&
              ` ${result.skippedNoEmailCount} user(s) have no email address and could not be revoked; they may still hold a live session.`}
            {result.failedCount > 0 &&
              ` ${result.failedCount} revocation(s) failed and those users may still be signed in.`}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Search companies"
          size="small"
          sx={styles.searchRow}
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
          }}
        />

        <Stack direction="row" spacing={3} sx={styles.columns}>
          <TableContainer sx={styles.companyList}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell align="right">Active users</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companiesLoading ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map((company) => (
                    <TableRow
                      key={company.uuid}
                      hover
                      selected={company.uuid === selectedCompanyUuid}
                      sx={styles.clickableRow}
                      onClick={() => {
                        handleSelectCompany(company.uuid);
                      }}
                    >
                      <TableCell>{company.name}</TableCell>
                      <TableCell align="right">
                        {company.activeUserCount}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={styles.usersPanel}>
            {selectedCompanyUuid === null ? (
              <Typography color="text.secondary" variant="body2">
                Pick a company to see its users.
              </Typography>
            ) : usersLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                {hasMore && (
                  <Alert severity="info">
                    Showing the first {users.length} of {totalCount} users.
                    Leaving the selection empty still targets all {totalCount}.
                  </Alert>
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              users.length > 0 &&
                              selectedUserUuids.length === users.length
                            }
                            indeterminate={
                              selectedUserUuids.length > 0 &&
                              selectedUserUuids.length < users.length
                            }
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.uuid} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedUserUuids.includes(user.uuid)}
                              onChange={() => {
                                handleToggleUser(user.uuid);
                              }}
                            />
                          </TableCell>
                          <TableCell>{user.name ?? "-"}</TableCell>
                          <TableCell>
                            {user.canRevokeSessions ? (
                              user.email
                            ) : (
                              <Tooltip title="No email address, so this user's session cannot be revoked">
                                <span>{user.email ?? "-"}</span>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            {user.isActive ? "Active" : "Archived"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Stack direction="row" spacing={2} sx={styles.actions}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={archiveUsers}
                        onChange={(event) => {
                          setArchiveUsers(event.target.checked);
                        }}
                      />
                    }
                    label="Also archive (blocks them logging back in)"
                  />
                  <Button
                    color="error"
                    disabled={revoking || totalCount === 0}
                    variant="contained"
                    onClick={() => {
                      setConfirmOpen(true);
                    }}
                  >
                    Revoke sessions
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        </Stack>

        <Dialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
          }}
        >
          <DialogTitle>Revoke sessions?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This signs out {targetDescription} on their next request.
              {archiveUsers
                ? " They will also be archived and cannot log back in."
                : " They are not archived, so anyone who knows their password can log straight back in."}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setConfirmOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              color="error"
              disabled={revoking}
              variant="contained"
              onClick={handleConfirmRevoke}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageLayout>
  );
};

export default UserSessionsAdminPage;
